/**
 * Check creation (route `/checks/new`; Feature: granular-crud-api, tasks 14.1, 14.2; Requirements
 * 11.1, 11.6, 11.7; Design: §11).
 *
 * `POST /v1/checks` through {@link useWrite}, so a failure displays the returned error and leaves the
 * page exactly as it was — the draft intact, no navigation (Req 11.6) — while a success navigates to
 * the new Check's detail page using the identifier the API assigned.
 *
 * A viewer never reaches the form: `canWrite` is false for them, so the page renders the read-only
 * explanation instead (Req 11.7). That is a display decision only — the API's role matrix is the
 * enforcement point.
 */

import { useNavigate } from 'react-router-dom';

import { useApiClient } from '../api/use-api-client';
import { useConfig } from '../config-context';
import { useCanWrite } from '../auth/use-can-write';
import { CheckForm } from '../components/CheckForm';
import { useWrite } from '../lib/use-write';

export function CheckCreatePage(): JSX.Element {
  const client = useApiClient();
  // The deployment's run locations (Req 8.2), for the form's location picker. Read here rather than
  // in CheckForm so the form keeps taking no context dependency.
  const { runnerRegions } = useConfig();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const write = useWrite();

  return (
    <section>
      <h1>New check</h1>
      {!canWrite ? (
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so checks cannot be created from the dashboard.
        </p>
      ) : (
        <CheckForm
          mode="create"
          availableLocations={runnerRegions}
          submitting={write.state.submitting}
          fieldErrors={write.state.fieldErrors}
          errorMessage={write.state.errorMessage}
          onSubmit={(payload) =>
            void write.run(
              () => client.createCheck(payload),
              (response) => navigate(`/checks/${encodeURIComponent(response.check.checkId)}`),
            )
          }
          onCancel={() => navigate('/')}
        />
      )}
    </section>
  );
}
